import { EXPO_PUBLIC_BACKEND_URL } from "@env";

const APP = "hermes";
const DEFAULT_BACKEND = "https://rycso.es/hermes";

function unwrap(value: string | undefined): string {
  let text = String(value || "").trim();
  if (
    (text.startsWith('"') && text.endsWith('"')) ||
    (text.startsWith("'") && text.endsWith("'"))
  ) {
    text = text.slice(1, -1).trim();
  }
  return text;
}

function env(name: string): string {
  if (typeof process !== "undefined" && process.env) {
    const v = unwrap(process.env[name]);
    if (v) return v;
  }
  if (name === "VITE_BACKEND_URL" || name === "EXPO_PUBLIC_BACKEND_URL") {
    return unwrap(EXPO_PUBLIC_BACKEND_URL);
  }
  return "";
}

export function backendUrl(): string {
  return (env("VITE_BACKEND_URL") || env("EXPO_PUBLIC_BACKEND_URL") || DEFAULT_BACKEND).replace(/\/+$/, "");
}

export function apiBase(): string {
  const base = backendUrl();
  if (base.endsWith("/api")) return base;
  return `${base}/api`;
}

async function fetchJson(url: string, init: RequestInit, timeoutMs = 20000): Promise<Response> {
  const controller = typeof AbortController !== "undefined" ? new AbortController() : undefined;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : undefined;
  try {
    return await fetch(url, {
      ...init,
      headers: init.headers,
      signal: controller?.signal,
    });
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Aviso de «sesión caducada»: el servidor ha respondido 401 a una petición hecha con token
 * (cuenta borrada o bloqueada, sesión cerrada desde el panel…). La app escucha esto para volver al login.
 */
type SessionExpiredListener = (token: string) => void;
const sessionExpiredListeners = new Set<SessionExpiredListener>();

export function onSessionExpired(listener: SessionExpiredListener): () => void {
  sessionExpiredListeners.add(listener);
  return () => {
    sessionExpiredListeners.delete(listener);
  };
}

/** Mensaje claro cuando el servidor falla sin explicar el motivo (p. ej. página de error HTML). */
export function httpErrorMessage(status: number): string {
  if (status === 401) return "Tu sesión ha caducado. Vuelve a iniciar sesión.";
  if (status === 402) return "Esta función es Premium. Hazte Premium desde tu perfil para usarla.";
  if (status === 403) return "No tienes permiso para hacer esto.";
  if (status === 404) return "Esta función no está disponible ahora mismo.";
  if (status === 429) return "Demasiados intentos seguidos. Espera unos minutos y vuelve a probar.";
  if (status >= 500) return "El servidor de Hermes no responde ahora mismo. Inténtalo de nuevo en unos minutos.";
  return `No se pudo completar la operación (código ${status}).`;
}

export async function cloudCall<T>(path: string, init: RequestInit & { token?: string } = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  headers.set("X-Rycso-App", APP);
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  // El token solo debe llegar a la API de Hermes: nada de «..», «//» ni «\» que lleven a otra ruta o host.
  if (/\.\.|\/\/|\\|%2e|%2f|%5c/i.test(path)) throw new Error("Ruta de API no válida");
  if (init.token) headers.set("Authorization", `Bearer ${init.token}`);
  const url = `${apiBase()}${path.startsWith("/") ? path : `/${path}`}`;
  const { token, ...rest } = init;
  const response = await fetchJson(url, { ...rest, headers });
  const data = (await response.json().catch(() => ({}))) as T & { error?: string };
  if (!response.ok) {
    if (response.status === 401 && token) {
      sessionExpiredListeners.forEach((listener) => {
        try {
          listener(token);
        } catch {
          /* un oyente roto no debe tapar el error real */
        }
      });
    }
    const serverMsg = typeof data.error === "string" ? data.error.trim() : "";
    // El 402 trae un código («premium_required»), no un texto para el usuario.
    throw new Error(response.status === 402 ? httpErrorMessage(402) : serverMsg || httpErrorMessage(response.status));
  }
  return data;
}

export async function sendResendMail(input: {
  to: string;
  subject: string;
  html: string;
  text: string;
}): Promise<{ sent: boolean; reason?: string }> {
  try {
    const remote = await cloudCall<{ sent?: boolean; ok?: boolean; mailSent?: boolean }>("/mail/send", {
      method: "POST",
      body: JSON.stringify(input),
    });
    if (remote.sent === true || remote.ok === true || remote.mailSent === true) {
      return { sent: true };
    }
  } catch (error) {
    const reason = error instanceof Error ? error.message : "";
    if (/Datos de correo/i.test(reason)) return { sent: false, reason };
  }

  // La clave de Resend vive solo en el servidor (nunca dentro de la app).
  return { sent: false, reason: "No se pudo enviar el correo. Revisa tu conexión." };
}
